export const injectGamicConsoleBridge = (html: string) => {
  const bridgeScript = `<script>
(() => {
  if (window.__gamicBridgeAttached) return;
  window.__gamicBridgeAttached = true;

  const toText = (value) => {
    if (typeof value === 'string') return value;
    try { return JSON.stringify(value); } catch { return String(value); }
  };
  const send = (level, values) => {
    try {
      window.parent.postMessage({
        __gamicPreviewLog: true,
        level,
        message: values.map(toText).join(' ')
      }, '*');
    } catch {}
  };

  ['log', 'info', 'warn', 'error', 'debug'].forEach((level) => {
    const original = console[level];
    console[level] = (...values) => {
      send(level, values);
      if (original) original.apply(console, values);
    };
  });

  const originalAssert = console.assert;
  console.assert = (condition, ...values) => {
    if (!condition) send('error', ['Assertion failed', ...values]);
    if (originalAssert) originalAssert.call(console, condition, ...values);
  };

  window.addEventListener('error', (event) => {
    const target = event.target;
    if (target && target !== window) {
      const src = target.src || target.href || target.currentSrc || target.baseURI || 'unknown';
      send('error', ['Resource failed to load:', src]);
      return;
    }
    send('error', [event.message, event.filename, event.lineno + ':' + event.colno]);
  }, true);

  window.addEventListener('unhandledrejection', (event) => {
    send('error', ['Unhandled promise rejection', event.reason]);
  });

  window.addEventListener('securitypolicyviolation', (event) => {
    send('error', ['CSP violation', event.violatedDirective, event.blockedURI]);
  });

  const NativeAudioContext = window.AudioContext || window.webkitAudioContext;
  if (NativeAudioContext) {
    const PatchedAudioContext = function(...args) {
      const ctx = new NativeAudioContext(...args);
      if (ctx.state === 'suspended') {
        send('warn', [
          'AudioContext started in suspended state. It may require user gesture due to autoplay policy.'
        ]);
      }
      if (ctx.addEventListener) {
        ctx.addEventListener('statechange', () => {
          send('info', ['AudioContext state:', ctx.state]);
        });
      }
      if (typeof ctx.resume === 'function') {
        const originalResume = ctx.resume.bind(ctx);
        ctx.resume = async (...resumeArgs) => {
          try {
            const result = await originalResume(...resumeArgs);
            send('info', ['AudioContext resumed']);
            return result;
          } catch (err) {
            send('warn', ['AudioContext resume failed', err]);
            throw err;
          }
        };
      }
      return ctx;
    };

    PatchedAudioContext.prototype = NativeAudioContext.prototype;
    window.AudioContext = PatchedAudioContext;
    if (window.webkitAudioContext) {
      window.webkitAudioContext = PatchedAudioContext;
    }
  }

  const originalFetch = window.fetch ? window.fetch.bind(window) : null;
  if (originalFetch) {
    window.fetch = async (...args) => {
      try {
        const response = await originalFetch(...args);
        if (!response.ok) {
          send('warn', ['Fetch failed', args[0], 'status', response.status]);
        }
        return response;
      } catch (err) {
        send('error', ['Fetch error', args[0], err]);
        throw err;
      }
    };
  }

  if (window.XMLHttpRequest) {
    const OriginalXHR = window.XMLHttpRequest;
    const open = OriginalXHR.prototype.open;
    const sendXhr = OriginalXHR.prototype.send;

    OriginalXHR.prototype.open = function(method, url, ...rest) {
      this.__gamicMethod = method;
      this.__gamicUrl = url;
      return open.call(this, method, url, ...rest);
    };

    OriginalXHR.prototype.send = function(...args) {
      this.addEventListener('error', () => {
        send('error', ['XHR error', this.__gamicMethod || 'GET', this.__gamicUrl || '']);
      });
      this.addEventListener('load', () => {
        if (this.status >= 400) {
          send('warn', ['XHR failed', this.__gamicMethod || 'GET', this.__gamicUrl || '', 'status', this.status]);
        }
      });
      return sendXhr.apply(this, args);
    };
  }

  send('info', ['Console bridge attached']);
})();
</script>`;

  if (html.includes('</head>')) {
    return html.replace('</head>', `${bridgeScript}</head>`);
  }
  return `${bridgeScript}${html}`;
};

const IGNORED_RESOURCE_PATTERNS = [
  './Funizzle_files/',
  '/Funizzle_files/',
  'Funizzle_files/',
];

export const shouldIgnoreGamicPreviewLog = (message: string) => {
  const normalizedMessage = message.trim();

  if (!normalizedMessage.startsWith('Resource failed to load:')) {
    return false;
  }

  return IGNORED_RESOURCE_PATTERNS.some((pattern) =>
    normalizedMessage.includes(pattern)
  );
};
